import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { animate } from 'motion';
import { useSearch } from '../hooks';
import classNames from 'classnames';

export interface IMobileSort {
  label: string;
  labelSortRelevance: string;
  labelSortAlphabetical: string;
  labelSortRecent: string;
}

const MobileSort: React.FC<IMobileSort> = ({
  label,
  labelSortRelevance,
  labelSortAlphabetical,
  labelSortRecent
}) => {
  const {
    state: { sort },
    updateSort
  } = useSearch();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const uuid = React.useMemo(() => uuidv4(), []);
  const panelEl = React.useRef<HTMLDivElement>(null);

  const togglePanel = React.useCallback(
    async (el: HTMLDivElement, inner: HTMLUListElement, expand: boolean) => {
      if (expand) {
        inner.style.display = 'block';

        animate(
          el,
          {
            height: `${inner.clientHeight}px`
          },
          {
            duration: 0.3
          }
        );
      } else {
        await animate(
          el,
          {
            height: 0
          },
          {
            duration: 0.3
          }
        ).finished;
        inner.style.display = 'none';
      }
    },
    []
  );

  React.useEffect(() => {
    const listEl = panelEl.current?.querySelector('ul');
    if (panelEl.current && listEl) {
      togglePanel(panelEl.current, listEl, isExpanded);
    }
  }, [isExpanded]);

  const getLabel = () => {
    switch (sort) {
      case 'relevance':
        return labelSortRelevance;
      case 'alphabetical':
        return labelSortAlphabetical;
      case 'recent':
        return labelSortRecent;
      default:
        return labelSortRelevance;
    }
  };

  return (
    <div className="search__facets-category search__facets-category--sort">
      <button
        type="button"
        className="search__facets-category__trigger"
        id={`trigger-${uuid}`}
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-controls={`panel-${uuid}`}
      >
        <div>
          {label} {getLabel()}
        </div>
        <svg role="presentation">
          <use xlinkHref="#chevron-down" />
        </svg>
      </button>

      <div
        ref={panelEl}
        role="region"
        className="search__facets-category__panel"
        id={`panel-${uuid}`}
        aria-labelledby={`trigger-${uuid}`}
      >
        <ul>
          <li>
            <button
              className={classNames([
                'search__facets-category__item',
                'search__facets-category__item--button',
                {
                  'search__facets-category__item--active':
                    !sort || sort === 'relevance'
                }
              ])}
              onClick={() => updateSort('relevance')}
            >
              {labelSortRelevance}
            </button>
          </li>
          <li>
            <button
              className={classNames([
                'search__facets-category__item',
                'search__facets-category__item--button',
                {
                  'search__facets-category__item--active':
                    sort === 'alphabetical'
                }
              ])}
              onClick={() => updateSort('alphabetical')}
            >
              {labelSortAlphabetical}
            </button>
          </li>
          <li>
            <button
              className={classNames([
                'search__facets-category__item',
                'search__facets-category__item--button',
                { 'search__facets-category__item--active': sort === 'recent' }
              ])}
              onClick={() => updateSort('recent')}
            >
              {labelSortRecent}
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default MobileSort;
